import { useState, useEffect } from 'react';
import { adminService } from '../../services/supabase';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const T = {
  primary:      '#FF4444',
  primaryDark:  '#e03333',
  gradient:     'linear-gradient(135deg, #FF4444, #FF6B00)',
  bg:           '#f5f7fa',
  surface:      '#ffffff',
  border:       '#e5e7eb',
  text:         '#1f2937',
  muted:        '#6b7280',
  grid:         '#f1f3f5',
};

const formatCurrency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatMonth = (ym) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};
const formatDay = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const StatTile = ({ label, value }) => (
  <div style={{
    background: T.surface, borderRadius: '14px', padding: '24px',
    border: `1px solid ${T.border}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  }}>
    <div style={{ fontSize: '28px', fontWeight: '800', color: T.primary, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '13px', color: T.muted, marginTop: '6px', fontWeight: '600' }}>{label}</div>
  </div>
);

const ChartCard = ({ title, data, xKey, xFormatter }) => (
  <div style={{
    background: T.surface, borderRadius: '14px', padding: '24px',
    border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  }}>
    <div style={{ fontSize: '14px', fontWeight: '700', color: T.text, marginBottom: '16px' }}>{title}</div>
    {data.length === 0 ? (
      <div style={{ padding: '40px 0', textAlign: 'center', color: T.muted, fontSize: '13px' }}>No orders in this period</div>
    ) : (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={T.grid} />
          <XAxis
            dataKey={xKey}
            tickFormatter={xFormatter}
            tick={{ fontSize: 11, fill: T.muted }}
            axisLine={{ stroke: T.border }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: T.muted }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={36}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,68,68,0.06)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;
              const row = payload[0].payload;
              return (
                <div style={{
                  background: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px',
                  padding: '8px 12px', fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '4px', color: T.text }}>{xFormatter(label)}</div>
                  <div style={{ color: T.muted }}>Orders: <strong style={{ color: T.text }}>{row.order_count}</strong></div>
                  <div style={{ color: T.muted }}>Revenue: <strong style={{ color: T.text }}>{formatCurrency(row.revenue)}</strong></div>
                </div>
              );
            }}
          />
          <Bar dataKey="order_count" fill={T.primary} radius={[4, 4, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const AdminReports = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error: err } = await adminService.getDashboardStats();
      if (err) {
        setError(err.message || 'Failed to load dashboard stats');
      } else {
        setStats(data);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: T.muted }}>Loading reports...</div>;
  }

  if (error) {
    return <div style={{ padding: '60px', textAlign: 'center', color: '#dc2626' }}>{error}</div>;
  }

  const perRestaurant = stats?.per_restaurant || [];
  const sortedRestaurants = [...perRestaurant].sort((a, b) => {
    const mult = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'restaurant_name') return a.restaurant_name.localeCompare(b.restaurant_name) * mult;
    return (a[sortBy] - b[sortBy]) * mult;
  });

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ col, label, align = 'left' }) => (
    <th
      onClick={() => toggleSort(col)}
      style={{
        padding: '10px 16px', textAlign: align, fontSize: '12px', fontWeight: '700',
        color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      {label}{sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div>
      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <StatTile label="Total Restaurants" value={stats.total_restaurants} />
        <StatTile label="Total Orders" value={stats.total_orders.toLocaleString('en-IN')} />
        <StatTile label="Total Revenue" value={formatCurrency(stats.total_revenue)} />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <ChartCard title="Orders — last 12 months" data={stats.orders_by_month} xKey="month" xFormatter={formatMonth} />
        <ChartCard title="Orders — last 30 days" data={stats.orders_by_day} xKey="day" xFormatter={formatDay} />
      </div>

      {/* Per-restaurant table */}
      <div style={{
        background: T.surface, borderRadius: '14px',
        border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 0', fontSize: '14px', fontWeight: '700', color: T.text }}>
          Orders & revenue by restaurant
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <SortHeader col="restaurant_name" label="Restaurant" />
                <SortHeader col="order_count" label="Orders" align="right" />
                <SortHeader col="revenue" label="Revenue" align="right" />
              </tr>
            </thead>
            <tbody>
              {sortedRestaurants.map((r) => (
                <tr key={r.restaurant_id} style={{ borderBottom: `1px solid ${T.grid}` }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: T.text }}>{r.restaurant_name}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: T.text, textAlign: 'right' }}>{r.order_count}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: T.text, textAlign: 'right', fontWeight: '600' }}>{formatCurrency(r.revenue)}</td>
                </tr>
              ))}
              {sortedRestaurants.length === 0 && (
                <tr><td colSpan={3} style={{ padding: '24px', textAlign: 'center', color: T.muted, fontSize: '13px' }}>No restaurants yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
